import { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { LogIn, LogOut, Settings as SettingsIcon } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { getMe, getLookups, setMyDefaultFacility, getDockBoardSignedIn, markSeen } from '@/api/client';
import SchedulerChatWidget from '@/components/SchedulerChatWidget';
import { useMe } from '@/auth/useMe';
import { Button } from '@/components/ui/button';
import { BrandLogo } from '@/components/BrandLogo';
import { CalendarPage } from '@/pages/Calendar';
import { CarrierAppShell } from '@/components/CarrierAppShell';
import { CarrierLoginPage } from '@/pages/carrier/CarrierLogin';
import { CarrierVerifyPage } from '@/pages/carrier/CarrierVerify';
import CarrierDashboardPage from '@/pages/carrier/CarrierDashboard';
import { DockBoardPage } from '@/pages/DockBoard';
import ReportsPage from '@/pages/Reports';
import DriverCheckInPage from '@/pages/DriverCheckIn';
import AppFooter from '@/components/AppFooter';
import { useBranding } from '@/lib/useBranding';
import { appFromSearch, goToApp, openPortal, pipelineUrl, WAREHOUSE_BOARD_CONFIG, withoutAppParam, type PortalApp } from '@/lib/appSwitch';
import type { TabId as SettingsTabId } from '@/components/SettingsDialog';
import { chosenCompanyId, companyCodeFromSearch, rememberCompany, withoutCompanyParam, TAB_APP, COMPANY_QUERY_PARAM } from '@/lib/company';

/**
 * Top-level app routing.
 *
 * Two distinct app surfaces under one frontend bundle:
 *
 *   /carrier/* → carrier portal (public, no Entra auth). Magic-link sign-in,
 *                dashboard for the carrier company's appointments.
 *
 *   anything else → internal Dock Scheduler app (Entra-gated). The original
 *                   app for Symbia schedulers and admins.
 *
 * The internal app's own tab navigation (Calendar today; more later) is
 * NOT route-driven — it's state-managed inside InternalApp. Adding new
 * top-level "areas" (e.g. /reports) can become routes later; today they're
 * tab switches.
 */
 
 export function App() {
    return (
        <BrowserRouter>
            <div className="h-full flex flex-col">
                <div className="flex-1 min-h-0 flex flex-col">
                    <Routes>
                        {/* Carrier portal — public routes wrapped in the lighter shell */}
                        <Route path="/carrier" element={<Navigate to="/carrier/login" replace />} />
                        <Route path="/carrier/login" element={<CarrierAppShell><CarrierLoginPage /></CarrierAppShell>} />
                        <Route path="/carrier/verify" element={<CarrierAppShell><CarrierVerifyPage /></CarrierAppShell>} />
                        <Route path="/carrier/dashboard" element={<CarrierAppShell><CarrierDashboardPage /></CarrierAppShell>} />
                        {/* ⭐ F212: the client portal — the same pages, the client's cut (Decision 366) */}
                        <Route path="/client" element={<Navigate to="/client/login" replace />} />
                        <Route path="/client/login" element={<CarrierAppShell portal="client"><CarrierLoginPage portal="client" /></CarrierAppShell>} />
                        <Route path="/client/verify" element={<CarrierAppShell portal="client"><CarrierVerifyPage /></CarrierAppShell>} />
                        <Route path="/client/dashboard" element={<CarrierAppShell portal="client"><CarrierDashboardPage portal="client" /></CarrierAppShell>} />
                        {/* Dock worker board — public kiosk, authorized by facility-network IP */}
                        <Route path="/board" element={<DockBoardPage />} />
                        {/* ⚠️ BEFORE the /* catch-all, or it falls through to
                            InternalApp and a driver gets an Entra login screen.
                            No auth wrapper: the gate is the facility IP, checked
                            server-side (arch §1D), same as /board. */}
                        <Route path="/checkin" element={<DriverCheckInPage />} />
                        {/* On a board hostname the ROOT is the board, so a TV can be
                            pointed at the bare host (see isBoardHostname). */}
                        {isBoardHostname() && <Route path="/" element={<DockBoardPage />} />}
                        {/* Internal Dock Scheduler app — Entra-gated */}
                        <Route path="/*" element={<InternalApp />} />
                    </Routes>
                </div>
                <ChromeFooter />
            </div>
        </BrowserRouter>
    );
}

/**
 * Hostnames (first DNS label) whose ROOT serves the dock board instead of the
 * Entra-gated scheduler app — e.g. dockview.symbia.com. A wall TV gets pointed
 * at the bare hostname once and never touched again; making it land on a login
 * screen would defeat the whole kiosk model.
 *
 * The board is ALSO always reachable at /board on every hostname, so this is
 * additive — nothing depends on DNS being right for the board to work.
 *
 * Deliberately a code constant, not a DB setting: this is routing, not
 * operator-tunable behavior. Adding a second board hostname is a one-word edit.
 */
const BOARD_HOSTNAMES = ['dockview'];

function isBoardHostname(): boolean {
    if (typeof window === 'undefined') return false;
    const label = window.location.hostname.split('.')[0].toLowerCase();
    return BOARD_HOSTNAMES.includes(label);
}

/**
 * The app footer everywhere EXCEPT the dock board — a wall display is
 * full-bleed, with no app chrome competing for the ~2 seconds someone
 * looks at it.
 */
function ChromeFooter() {
    const { pathname } = useLocation();
    const onBoard = pathname === '/board'
        || pathname.startsWith('/board/')
        || (isBoardHostname() && pathname === '/');
    if (onBoard) return null;
    return <AppFooter />;
}


// =============================================================================
// Internal app (Entra-gated)
// =============================================================================

/**
 * =============================================================================
 * ⭐⭐⭐ TWO TIERS OF NAVIGATION — APPS, THEN VIEWS (decision 192, 2026-09-20)
 * =============================================================================
 *
 * ⭐ Pete: *"having the tabs be the other apps like Calendar says Scheduler, the
 * Reporting and Claude are apps but also Labor and Client would be on that list.
 * The problem becomes when we open other screens in the app … **Can we have a sub
 * menu of tabs underneath the main app tab or something like that?**"*
 *
 * ⛔⛔ THE SPLIT IS DRAWN ON THE **TOKEN BOUNDARY**, NOT AN ARBITRARY ONE, AND THAT
 * IS THE WHOLE REASON TWO ROWS ARE LEGIBLE INSTEAD OF CONFUSING:
 *
 *   TIER 1 — APPS.  Each is its own hostname with its own Entra app registration
 *                   (§1AV.2's inventory). Switching is a CROSS-ORIGIN NAVIGATION
 *                   with a fresh token. ⚠️ Every tab in this row costs a reload.
 *   TIER 2 — VIEWS. Inside one app, one SPA. ⚠️ Every tab in this row is instant.
 *
 * ** A CONTROL'S SHAPE IS A PROMISE ABOUT WHAT IT COSTS. A TAB THAT UNLOADS THE
 *    PAGE IS A LIE — so the two rows are styled differently ON PURPOSE (pills
 *    above, underlines below). **
 *
 * ⚠️ NOTHING NAVIGATES YET. Pete, 2026-09-20: *"going nowhere except for scheduler
 * right now … then go back to the other tabs like labor and client, etc and figure
 * out how those will work."* ⛔ So Labor / Client / Pipeline render the SAME
 * roadmap placeholder that Reporting and Claude have had since 2026-08-25 — see
 * `ComingSoon`. They are deliberately NOT wired to `dbo.CompanyApp` yet; §1AV.177
 * records that the launcher's contents are gates 2 and 3 of the six-gate order,
 * and building the query before the apps exist would be inventing the requirement.
 *
 * ⛔⛔ AND THIS LIST IS **NOT** `dbo.App`. It is four hard-coded labels standing in
 * for a query that does not exist yet. When it becomes real:
 *   ** A GRANT SCREEN MUST NOT OFFER AN APP THE COMPANY DOES NOT HAVE. Not
 *      disabled-with-a-tooltip: ABSENT. ** (§1A.2.6h, applies verbatim here)
 *   ⛔ and `PLATFORM` must never appear — `IsAssignableToCompany = 0`.
 */
/* F818: another Bardotek app opens signed in as the same account (login_hint) - same tab. */
function withHint(url: string, email: string | null | undefined): string {
    try { const u = new URL(url); if (email) u.searchParams.set('login_hint', email); return u.toString(); } catch { return url; }
}

/* F802: the C&W WMS address (the WMS tab). */
const WMS_URL: string = (import.meta.env.VITE_WMS_URL as string | undefined) || 'https://cw.bardotek.com';

/* ⭐ F218 — Pete: *"Lets put Client and Carrier on this tab (client already is) and for
   now, have it open a new tab in the browser to the client or carrier portal."* Client and
   Carrier carry `portal`: their pill opens that portal in a NEW browser tab (appSwitch
   openPortal) and this page stays where it is. They are not apps inside the Scheduler, so
   they have no views and are never the active app. */
const APP_TABS = [
    { id: 'scheduler', label: 'Scheduler' },
    { id: 'labor',     label: 'Labor' },
    /* F226: the portal opens in THIS tab (like Pipeline), no longer a new one */
    { id: 'client',    label: 'Client',  portal: 'client' },
    { id: 'carrier',   label: 'Carrier', portal: 'carrier' },
    { id: 'pipeline',  label: 'Pipeline' },
] as const;

type AppTabId = Exclude<(typeof APP_TABS)[number]['id'], PortalApp>;

/**
 * ⭐ TIER 2 — the views inside each app. Add an entry here and a render branch in
 * <PageRouter/>; the union below is derived, so the compiler stops you naming a
 * view that does not exist.
 *
 * ⛔ `calendar` / `reports` / `claude` ARE UNCHANGED — they were never apps, they
 * were always views of the Scheduler, and this tier is what finally says so.
 * ⭐ Pete: *"Calendar says Scheduler"* — the APP is the Scheduler; `Calendar` is one
 * of its screens.
 */
const VIEW_TABS = {
    /* ⭐ F218 — Pete: *"Lets remove the Claude link on the dock scheduler app and put the
       warehouse tab next to the calender tab and reporting at the end."* → Calendar ·
       Warehouse · Reporting. The Claude placeholder is gone (its roadmap item #252 stays
       in the open items). */
    scheduler: [
        { id: 'calendar', label: 'Calendar' },
        { id: 'warehouse', label: 'Warehouse' },
        /**
         * ⭐ TWO PLACEHOLDERS, ASKED FOR BY NAME. Pete, 2026-08-25: *"lets put two more
         * tab place holders next to Calendar, One called Claude for an AI interface and
         * one for Reporting. Put Reporting first, then claude."*
         *
         * ⛔ AND THEY SAY THEY ARE PLACEHOLDERS WHEN YOU LAND ON THEM. A tab that opens
         * a blank page reads as broken; a tab that says "not built yet, here is what it
         * will be" reads as a roadmap. Reporting's shape is #203 and is not settled, so
         * inventing a screen for it now would be inventing the requirement too.
         */
        { id: 'reports', label: 'Reporting' },
        /**
         * ⭐ F154 (Decision 330) — Pete: *"add a link for …/board?cfg=main … next to
         * Calendar and Reporting, on that line, not a new app at the top? Tab should
         * be called Warehouse"*.
         * ⭐ F161 (Decision 334) — ⛔⤴ ~~it sent this browser tab to /board~~. Pete: *"It
         * should load under the Calendar, Reporting, Warehouse and Claude line, replacing
         * the scheduler screen. Still in scheduler, still on the same tab"*. It is a VIEW:
         * PageRouter draws the floor board (embedded) and Calendar is one click back.
         * F218: Warehouse now sits right after Calendar (above).
         */
    ],
    /* ⭐ F226 — Pete: *"Lets change the Labor screen to say Labor Management instead of
       Laborclock"*. One view; the Laborclock placeholder is gone (the clock itself runs on
       facility devices — laborclock.symbia.com). */
    labor: [
        { id: 'labor-mgmt', label: 'Labor Management' },
    ],
    pipeline: [{ id: 'pipeline-home', label: 'Pipeline' }],
} as const satisfies Record<AppTabId, readonly { id: string; label: string }[]>;

type ViewTabId = (typeof VIEW_TABS)[AppTabId][number]['id'];

/** Gear-menu items. Each opens the Settings dialog on a specific tab. */
// ⚠️ THIS UNION AND THE TAB STRIP IN SettingsDialog.tsx ARE ONE LIST IN TWO
// PLACES. A tab added to only one of them is either invisible (no menu item) or
// unreachable (no tab to land on). Sessions 43 and 44 added 'documents' and
// 'door-text' to the dialog and not here, so both config screens Pete asked for
// shipped with no way to open them — caught by the gear-menu/tab-strip guard in
// deploy-frontend-dockboard.ps1, which is the only reason it was found.
// ⛔ 'branding' IS NOT IN THIS UNION (removed 2026-09-07 -- see the gear menu
//    below). Leaving it here would let the entry be added back with no type
//    error, which is the only thing standing between the menu and the tab strip
//    at compile time.
// ⭐ IMPORTED, NOT RETYPED (2026-09-09). ~~A local union listing every tab~~ lived
// here and drifted from SettingsDialog's the first time a tab was added to only
// one of them — see that file's note on `TabId`. ⚠️ The comment above still
// stands: the compiler stops you naming a tab that does not exist, and cannot
// make you add the menu item.

interface GearMenuSection {
    label: string;
    items: { settingsTab: SettingsTabId; label: string; requiredRole: 'scheduler' | 'admin' }[];
}

const GEAR_MENU: GearMenuSection[] = [
    {
        label: 'OPERATIONS',
        items: [
            { settingsTab: 'facility-hours',      label: 'Facility Hours',       requiredRole: 'scheduler' },
            { settingsTab: 'appointment-limits',  label: 'Appointments',         requiredRole: 'scheduler' },
            // ⚠️ BOTH PLACES, ALWAYS. On 2026-08-19 two config screens shipped with a
            // tab-strip entry and no gear item, so they rendered perfectly and were
            // unreachable. See SettingsDialog's TabId list.
            { settingsTab: 'appointment-fields',  label: 'Appointment Fields',   requiredRole: 'scheduler' },
            // ⚠️ BOTH PLACES, ALWAYS — and SCHEDULER_TABS in SettingsDialog too, or
            // canSee() bounces the user straight back to facility-hours.
            { settingsTab: 'automated-texts',     label: 'Automated Texts',      requiredRole: 'scheduler' },
            { settingsTab: 'reminder-schedule',   label: 'Reminder Schedule',    requiredRole: 'scheduler' },
            { settingsTab: 'door-dedications',    label: 'Door Dedications',     requiredRole: 'scheduler' },
            { settingsTab: 'facility-directions', label: 'Directions',           requiredRole: 'scheduler' },
            { settingsTab: 'carrier-users',       label: 'Portal Users (Carriers & Clients)', requiredRole: 'scheduler' },
            { settingsTab: 'drivers',             label: 'Drivers',              requiredRole: 'scheduler' },
        ],
    },
    {
        label: 'ADMIN',
        items: [
            { settingsTab: 'facilities',    label: 'Facilities',   requiredRole: 'admin' },
            { settingsTab: 'doors',         label: 'Dock Doors',   requiredRole: 'admin' },
            { settingsTab: 'capabilities',  label: 'Capabilities', requiredRole: 'admin' },
            { settingsTab: 'carriers',      label: 'Carriers',     requiredRole: 'admin' },
            { settingsTab: 'clients',       label: 'Clients',      requiredRole: 'admin' },
            { settingsTab: 'users',         label: 'Users',        requiredRole: 'admin' },
            { settingsTab: 'chat-groups',   label: 'Chat Groups',  requiredRole: 'admin' },
            { settingsTab: 'status-colors', label: 'Status Colors', requiredRole: 'admin' },
            // ⚠️ BOTH PLACES, ALWAYS — and this line is late BECAUSE the note above was
            // not read. The Right-Side Toolbar tab shipped 2026-09-08 with a tab-strip
            // entry and NO gear item, so it rendered perfectly and was unreachable from
            // the menu. Pete, 2026-09-09: *"doesn't show on the sprocket list either."*
            // ⛔ THIRD OCCURRENCE of the 2026-08-19 failure, in the file that documents it.
            // ⭐ Which cards appear down the right of the schedule, per facility
            // (migration 172). Next to Status Colours because both answer "what the
            // scheduler's screen LOOKS like", not what it contains.
            { settingsTab: 'rail-panels',   label: 'Right-Side Toolbar', requiredRole: 'admin' },
            { settingsTab: 'board-screens', label: 'Dockview Screens', requiredRole: 'admin' },
            { settingsTab: 'driver-checkin', label: 'Driver Check-in', requiredRole: 'admin' },
            // Paperwork: how scans are named, what may be stored, the scan-folder
            // hint, and the load-received stamp (#101, #110).
            { settingsTab: 'documents',     label: 'Documents',        requiredRole: 'admin' },
            // ⚠️ BOTH PLACES, ALWAYS — see the note above. Which number on a scanned
            // page is used to find the load, per client / carrier / facility (#246).
            { settingsTab: 'ocr-ranking',   label: 'OCR Ranking',      requiredRole: 'admin' },
            // ⭐ #317 — what a load is not finished without. Admin-only: it decides
            // what holds a truck at a gate, across every load for a client.
            { settingsTab: 'departure-checklists', label: 'Departure Checklists', requiredRole: 'admin' },
            // ⭐ #317 step 5 — the standing documents a checklist requirement asks for.
            { settingsTab: 'handouts', label: 'Handouts', requiredRole: 'admin' },
            // ⚠️ BOTH PLACES, ALWAYS. ⭐ #324 — where loads are delivered to. Next to
            // Handouts because a handout is scoped BY consignee code and the delivery
            // pin resolves the same code to an address.
            { settingsTab: 'consignees', label: 'Consignees', requiredRole: 'admin' },
            /**
             * ⛔⛔ THE BRANDING ENTRY WAS REMOVED 2026-09-07. Do not put it back.
             *
             * Pete: "it should be one screen in pipeline driving all the sub apps."
             *
             * ** Branding is edited in ONE place now -- Pipeline's Branding screen,
             *    which writes dbo.Company / dbo.CompanyAppBranding / dbo.App and is
             *    resolved for every app by dbo.vBrandingResolved (migration 084).
             *    This app READS that (useBranding / GET /api/brand) and no longer
             *    writes it: the two mgmt/branding routes are gone too.
             *
             * ⚠️ THE COMMENT ABOVE THIS ONE USED TO SAY "BOTH PLACES, ALWAYS" -- and
             * it was right: a settings destination lives in this menu AND in
             * SettingsDialog's tab strip. Removing the tab and leaving this entry is
             * a gear-menu item that opens Settings on a tab that does not exist.
             * ⭐ deploy-frontend-dockboard.ps1 compares the two lists and aborts on
             * exactly that drift, which is how this was caught rather than shipped.
             */
            // The wording of the dock-door and paperwork-ready texts a driver
            // receives, per facility (#103).
            { settingsTab: 'door-text',     label: 'Dock Door Text',   requiredRole: 'admin' },
        ],
    },
];

function InternalApp() {
    return (
        <>
            <UnauthenticatedTemplate>
                <SignInScreen />
            </UnauthenticatedTemplate>

            <AuthenticatedTemplate>
                <AppShell />
            </AuthenticatedTemplate>
        </>
    );
}

function SignInScreen() {
    const { signIn } = useAuth();
    /**
     * ⭐ THE SIGN-IN SCREEN IS BRANDED, WHICH IS WHY `GET brand` TAKES NO TOKEN.
     * It is the first screen every customer's staff ever sees; leaving it as ours
     * while the app behind it is theirs is the one place white-labelling most
     * obviously fails.
     *
     * ⚠️ AND WITH SEVERAL COMPANIES ACTIVE IT DELIBERATELY SHOWS NOBODY'S BRAND.
     * The server refuses to guess here rather than putting one customer's logo in
     * front of another customer's staff — see brandingRepository.
     */
    // ⭐ EXPLICIT, though DOCK is the default. The app code is a real choice now
    //    (migration 077 gave CARRIER its own), and a defaulted one is invisible.
    const branding = useBranding('DOCK');

    return (
        <div className="h-full grid place-items-center">
            <div className="text-center max-w-md px-6 animate-fade-in">
                {branding.logo ? (
                    <img src={branding.logo} alt={branding.companyName}
                        className="mx-auto mb-6 h-16 w-auto max-w-[220px] object-contain" />
                ) : (
                    /* F810 (Pete: 'this needs to say Bardotek now'): before sign-in no company is known - the platform's mark */
                    <img src="/bardotek-tree.png" alt="Bardotek" className="mx-auto mb-6 h-16 w-auto max-w-[220px] object-contain" />
                )}
                <h1 className="font-serif-display text-[28px] font-medium tracking-tight">
                    {branding.companyName} {branding.appName}
                </h1>
                <p className="text-muted-foreground mt-2 mb-8 text-sm">
                    Sign in with your {branding.companyName} account to schedule dock door appointments.
                </p>
                <Button onClick={signIn} size="lg" className="px-8">
                    <LogIn className="h-4 w-4" />
                    Sign in with Microsoft
                </Button>
                <p className="text-xs text-muted-foreground mt-6">
                    Carrier? Use the{' '}
                    <a href="/carrier/login" className="underline">
                        Carrier Portal
                    </a>{' '}
                    instead.
                </p>
            </div>
        </div>
    );
}

/**
 * ⭐ F263 — THE WAREHOUSE TAB READS THE BOARD WITH THE PERSON'S SIGN-IN.
 * Pete, relaying James Grieco: *"when I click warehouse, it says that I am not
 * registered for the facility"* → popup *"Work anywhere when signed in"*. The board
 * is the same component and the same server query as the wall TV; only the read
 * changes: mgmt/board with this person's token, for their default facility (the
 * facility box at the top right), and it follows that box when it changes. With no
 * default and one facility, the server picks it; with several, the board says to
 * choose. The wall TVs (/board, kiosk/board) are untouched.
 */
function SignedInWarehouseBoard({ cfg }: { cfg: string }) {
    const { getToken } = useAuth();
    const [facility, setFacility] = useState<string | null | undefined>(undefined);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const me = await getMe(await getToken());
                if (alive) setFacility(me.defaultFacilityCode ?? null);
            } catch {
                if (alive) setFacility(null);
            }
        })();
        const onChange = (e: Event) => {
            const code = (e as CustomEvent<{ facilityCode: string | null }>).detail?.facilityCode ?? null;
            setFacility(code);
        };
        window.addEventListener('dockscheduler:default-facility-changed', onChange);
        return () => { alive = false; window.removeEventListener('dockscheduler:default-facility-changed', onChange); };
    }, [getToken]);

    const loader = useCallback(
        async (cfgKey: string | null, signal?: AbortSignal) =>
            getDockBoardSignedIn(await getToken(), cfgKey, facility ?? null, signal),
        [getToken, facility],
    );

    // ⚠️ Wait for the default facility before the first read, or the board would
    //    load once without it and then again with it.
    if (facility === undefined) return null;
    return <DockBoardPage embedded cfg={cfg} loader={loader} />;
}

/**
 * F802 (Decision 467) - which company. Shown only when the person holds a Dock role at more than one.
 * Picking reloads the page so every screen, cache and facility list starts again on that company.
 */
function CompanyPicker({ me }: { me: ReturnType<typeof useMe> }) {
    const list = me?.companies ?? [];
    if (list.length < 2 || typeof me?.companyId !== 'number') return null;
    return (
        <div className="flex items-center justify-end gap-1 text-[11px] text-muted-foreground mt-0.5">
            <span>Company:</span>
            <select
                value={me.companyId}
                onChange={(e) => { rememberCompany(Number(e.target.value)); window.location.reload(); }}
                title="The company you are working in - only companies where you hold a Dock role"
                aria-label="Company"
                className="rounded border border-border bg-transparent px-1 py-0.5 text-[11px] max-w-[150px] focus:outline-none focus:ring-1 focus:ring-primary"
            >
                {list.map((c) => <option key={c.companyId} value={c.companyId}>{c.companyName}</option>)}
            </select>
        </div>
    );
}

function DefaultFacilityPicker({ getToken }: { getToken: () => Promise<string> }) {
    const [facilities, setFacilities] = useState<{ FacilityCode: string; DisplayName: string }[]>([]);
    const [value, setValue] = useState<string>('');
    const [saving, setSaving] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const token = await getToken();
                const [me, lookups] = await Promise.all([getMe(token), getLookups(token)]);
                if (cancelled) return;
                setFacilities(lookups.facilities.map((f) => ({ FacilityCode: f.FacilityCode, DisplayName: f.DisplayName })));
                setValue(me.defaultFacilityCode ?? '');
                setLoaded(true);
            } catch {
                if (!cancelled) setLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    }, [getToken]);

    // Nothing to choose from (e.g. non-scheduler) → don't render.
    if (!loaded || facilities.length === 0) return null;

    const onChange = async (v: string) => {
        const prev = value;
        setValue(v);
        setSaving(true);
        try {
            const token = await getToken();
            await setMyDefaultFacility(token, v || null);
            // Let the calendar (and anything else open) switch immediately.
            window.dispatchEvent(new CustomEvent('dockscheduler:default-facility-changed', { detail: { facilityCode: v || null } }));
        } catch {
            setValue(prev); // revert on failure
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex items-center justify-end gap-1 text-[11px] text-muted-foreground mt-0.5">
            <span>Default:</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={saving}
                title="Your default facility — used across the scheduler and settings"
                className="rounded border border-border bg-transparent px-1 py-0.5 text-[11px] max-w-[150px] focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            >
                <option value="">None</option>
                {facilities.map((f) => (
                    <option key={f.FacilityCode} value={f.FacilityCode}>{f.DisplayName}</option>
                ))}
            </select>
        </div>
    );
}

/**
 * ⛔ BrandLogo MOVED TO components/BrandLogo.tsx (2026-09-07).
 *
 * ** It was private to this file, so CarrierAppShell could not import it and
 *    hardcoded <SymbiaLogoMark> instead -- the carrier portal ignored
 *    branding.logo completely while the Branding screen previewed one. Every
 *    shell that draws a header imports the shared component now.
 */

function AppShell() {
    const { name, email, signOut, getToken } = useAuth();
    const me = useMe();
    const branding = useBranding('DOCK');
    /* ⭐ F172: Pipeline's Labor / Client tabs arrive with `?app=labor|client`. Open on
       that app (its first view), then take the parameter off the address bar. */
    const [activeApp, setActiveApp] = useState<AppTabId>(() => appFromSearch(window.location.search) ?? 'scheduler');
    const [activeTab, setActiveTab] = useState<ViewTabId>(() => VIEW_TABS[appFromSearch(window.location.search) ?? 'scheduler'][0].id);
    useEffect(() => {
        const next = withoutAppParam(window.location.href);
        if (next) window.history.replaceState(window.history.state, '', next);
    }, []);

    /**
     * ⭐ F802 (Decision 467): the company. The API resolved it from the person's DOCK grants (the
     * remembered pick when it is one of theirs, else their default). Here: a `?company=CW` link from
     * another app switches to that company when it is one of theirs; otherwise remember what the API
     * used, so the next visit opens there (Pete F802: 'Remember last').
     */
    const [linkCode] = useState(() => companyCodeFromSearch());
    useEffect(() => {
        const next = withoutCompanyParam(window.location.href);
        if (next) window.history.replaceState(window.history.state, '', next);
    }, []);
    useEffect(() => {
        if (!me || typeof me.companyId !== 'number') return;
        const linked = linkCode ? (me.companies ?? []).find((c) => c.companyCode.toUpperCase() === linkCode) : undefined;
        if (linked && linked.companyId !== me.companyId) { rememberCompany(linked.companyId); window.location.reload(); return; }
        if (chosenCompanyId() !== me.companyId) rememberCompany(me.companyId);
    }, [me, linkCode]);
    /**
     * F823 (Pete: "history of their logins"): the platform's sign-in history. Said once when the app opens,
     * again when Labor is opened, and every 10 minutes while this tab is visible - the Pipeline keeps one
     * row per session (a new row after 30 minutes away). Never in the way: the answer is ignored.
     */
    const seenApp: 'DOCK' | 'LABOR' = activeApp === 'labor' ? 'LABOR' : 'DOCK';
    useEffect(() => {
        if (!me || typeof me.companyId !== 'number') return undefined;
        const say = () => { void getToken().then((t) => markSeen(t, seenApp)).catch(() => undefined); };
        say();
        const timer = window.setInterval(() => { if (document.visibilityState === 'visible') say(); }, 10 * 60 * 1000);
        return () => window.clearInterval(timer);
    }, [me?.companyId, seenApp, getToken]);   // eslint-disable-line react-hooks/exhaustive-deps
    const suiteCo = me?.suite?.companies.find((c) => c.companyId === me.companyId) ?? null;
    const companyCode = (me?.companies ?? []).find((c) => c.companyId === me?.companyId)?.companyCode ?? null;
    /* F802 / Pete F733: an app the company does not have (or the person holds no role in there) stays
       on the header GREYED - Pete's call over 1AV.177's 'absent', header only. The API refuses it anyway. */
    const wmsOpen = !!suiteCo && (suiteCo.apps.find((a) => a.appCode === 'WMS')?.open ?? false);
    const tabOpen = (id: string): boolean => {
        const code = TAB_APP[id];
        if (!code || !suiteCo) return true;
        return suiteCo.apps.find((a) => a.appCode === code)?.open ?? false;
    };

    /**
     * ⛔⛔ CHANGING THE APP MUST RESET THE VIEW, AND IT IS NOT A CONVENIENCE.
     *
     * `activeTab` is a union across ALL apps' views, so without this you can hold
     * `app='labor'` and `view='calendar'` at the same time — a state the type system
     * is perfectly happy with and which renders **the dock calendar underneath the
     * Labor tab.** ⚠️ Nothing on screen would say it was wrong.
     *
     * ** A CONTAINER THAT CHANGES MUST RESET WHAT IT CONTAINS, OR THE TWO DRIFT INTO
     *    A COMBINATION NEITHER OF THEM CHECKS. **
     *
     * ⭐ `[0]` IS SAFE BY CONSTRUCTION — `satisfies` forces every app to declare at
     * least one view, so there is always a first one to land on.
     */
    const companyCodeRef = useRef<string | null>(null);
    companyCodeRef.current = companyCode;
    const emailRef = useRef<string | null>(null);
    emailRef.current = email || null;
    const chooseApp = useCallback((id: AppTabId) => {
        /**
         * ⭐⭐ PIPELINE, IN THIS SAME TAB (Decision 243 — reverses 234's two tabs).
         * Pete: *"the same browser tab, scheduler turns into Pipeline on the
         * screen"*. The pill sends this tab to Pipeline; Pipeline's Scheduler
         * button sends it back (Back works too).
         * ⚠️ Only with no address configured does the Pipeline view open here,
         * and it says so.
         */
        if (id === 'pipeline') {
            const url = pipelineUrl();
            if (url) {
                // F802: Pipeline opens on the same company (it re-checks it against the person's own).
                let target = url;
                try {
                    const u = new URL(url); if (companyCodeRef.current) u.searchParams.set(COMPANY_QUERY_PARAM, companyCodeRef.current);
                    if (emailRef.current) u.searchParams.set('login_hint', emailRef.current);   // F818: signed in there without a click
                    target = u.toString();
                } catch { /* as configured */ }
                goToApp(target); return;
            }
        }
        setActiveApp(id);
        setActiveTab(VIEW_TABS[id][0].id);
    }, []);

    /* ⛔⤴ ~~`window.name = SCHEDULER_WINDOW`~~ — removed with the two-tab design
       (Decision 243): one tab needs no name to be found by. */

    /**
     * ⭐ THE HEADER CENTRE IS THE PAGE'S TITLE, AND THE CALENDAR'S IS CONFIGURABLE.
     *
     * Pete, 2026-08-25: *"underneath Calendar it says Dock Schedule. this should be
     * configurable to but it should be moved between Symbia Dock and Pete Lombardo
     * Login, Centered and big and bold like it is… It will give us more real estate
     * too."*
     *
     * ⭐ THE REAL ESTATE IS THE POINT. That title used to be an <h1> inside the
     * scrolling page, so it cost ~56px on every screen at every scroll position.
     * The 64px bar was already there and had a hole in the middle.
     *
     * ⛔ ONE RULE, NOT TWO: the centre is whatever page you are on. Only the
     * calendar's name is a branding setting, because it is the only one a company
     * would call something else ("Dock Schedule", "Yard Plan", "Door Board"). A
     * second configurable name per tab would be four settings nobody would ever
     * change.
     */
    /* ⭐ LOOKED UP WITHIN THE ACTIVE APP (2026-09-20). `NAV_TABS.find` was a search
       over ONE flat list; with two tiers the same id could in principle appear under
       two apps, and a flat search would find whichever came first.
       ⚠️ The `?? schedulerTitle` fallback is kept: it is what renders for the instant
       between an app changing and its first view being selected. */
    const centreTitle = activeTab === 'calendar'
        ? branding.schedulerTitle
        : ((VIEW_TABS[activeApp] as readonly { id: string; label: string }[])
            .find(t => t.id === activeTab)?.label ?? branding.schedulerTitle);

    return (
        <>
            <header className="app-hdr">
                <div className="app-hdr-inner is-wide">
                    {/**
                      * ---- ⭐⭐ TIER 1: THE APPS, ON THE BRAND ROW (Pete, 2026-09-20) ----
                      *
                      * ⭐ *"I like your apps on the brand row better. **conserves
                      * space**."* ⛔ And it costs NOTHING in height: `.app-hdr-inner` is a
                      * fixed 64px `1fr auto 1fr` grid, the brand block is ~40px tall, and
                      * this cell was carrying whitespace.
                      *
                      * ⭐⭐ AND THE CENTRE TITLE STAYS CENTRED, which is not luck. The
                      * grid is `1fr auto 1fr`, so the middle column is centred in the
                      * VIEWPORT regardless of what the outer cells hold — the note below
                      * explains why that matters. ⛔ A flex row with `margin:auto` would
                      * have drifted the moment a fourth app was added.
                      *
                      * ⚠️ THE APP TABS ATTACH TO THE APP LINE, NOT THE COMPANY. The two
                      * lines are *"THE COMPANY, then the APP — two different facts"*, and
                      * a control that looked like it switched the COMPANY would be a far
                      * bigger and wronger promise, especially to a SuperAdmin who really
                      * does span companies.
                      *
                      * ⛔ `text-[13px]` IS EXPLICIT, AND NOT A STYLE PREFERENCE. Pete:
                      * *"The main tabs should be the size of Calendar, Reporting - like it
                      * is today"* — 13px. `index.css`'s base layer carries
                      * `input, select, button, textarea { font-size: 14px }`, an ELEMENT
                      * rule that beats any inherited value, so a size on the <nav> alone
                      * would do nothing at all.
                      *   ** A CLASS ON THE PARENT ONLY WINS WHERE NOTHING ON THE CHILD
                      *      SAYS OTHERWISE — AND A BASE-LAYER ELEMENT RULE ON `button` IS
                      *      SOMETHING ON THE CHILD. **
                      * ⚠️ Tailwind's `@layer utilities` (index.css L518) comes after
                      * `@layer base` (L22), so the utility wins. Layer order, not
                      * specificity — four rounds were lost to that distinction on 09-19.
                      *
                      * ⚠️ `hidden lg:flex` — below 1024px the apps fold away rather than
                      * collide with a long company name. The strip below still reaches
                      * every view of the CURRENT app, so nothing becomes unreachable;
                      * only switching apps needs the wider window.
                      */}
                    <div className="flex min-w-0 items-center gap-6">
                        <div className="app-brand">
                            <BrandLogo logo={branding.logo} alt={branding.companyName} />
                            <div>
                                {/* ⚠️ THE COMPANY, then the APP. Two different facts. */}
                                <div className="app-brand-title">{branding.companyName}</div>
                                <div className="app-brand-sub">{branding.appName}</div>
                            </div>
                        </div>

                        <nav className="hidden items-center gap-1 lg:flex">
                            {/* ⭐ F264 (Pete: "Admins only"): the Client / Carrier pills are the CUSTOMER
                                portals' sign-in. Staff who clicked them (Steve Buckman) were asked for an
                                email and waited for a link that only portal users get. Admins keep them. */}
                            {APP_TABS.filter((app) => !('portal' in app) || !!me?.isAdmin).map((app) => (
                                <button
                                    key={app.id}
                                    type="button"
                                    /* ⭐ F218: Client / Carrier open their portal in a new browser tab */
                                    onClick={() => { if (!tabOpen(app.id)) return; if ('portal' in app) openPortal(app.portal); else chooseApp(app.id); }}
                                    disabled={!tabOpen(app.id)}
                                    title={!tabOpen(app.id) ? `${app.label} is not set up for ${suiteCo?.name ?? 'this company'}` : 'portal' in app ? `Opens the ${app.label.toLowerCase()} portal` : undefined}
                                    aria-current={activeApp === app.id ? 'page' : undefined}
                                    /**
                                      * ---- ⭐⭐ THE ONE THING ON THIS ROW THAT WAS NOT BRANDABLE ----
                                      *
                                      * ⭐ Pete, 2026-09-20: *"I think you had these in symbia blue on the
                                      * design you showed me. Can we do that. And are these part of the config
                                      * screen for the header in pipeline?"*
                                      *
                                      * ⛔⛔ MEASURED, AND THE ANSWER WAS NO. Every sibling on this row
                                      * resolves through a `--brand-*` variable — `.app-brand-title`,
                                      * `.app-brand-sub`, `.app-page-title`, `.app-tab`, `.app-identity` — and
                                      * the APP tabs were raw Tailwind, because they went in the night before
                                      * as visual structure (§1AV.177) and nobody wired them up.
                                      *   ** A CONTROL THAT SHIPPED AS A PLACEHOLDER KEEPS ITS PLACEHOLDER
                                      *      STYLING UNTIL SOMEBODY LOOKS. THE ROW AROUND IT IS THE SPEC. **
                                      *
                                      * ⭐ So the colours move to `--brand-apptab-*` in `index.css`, with
                                      * Pete's `#1d4ed8` as the FALLBACK. ⛔ The variable is never overridden
                                      * here — that is what made `--brand-subnav-size` wrong the same week.
                                      *   ** A DEFAULT IS A SETTING'S FALLBACK, NOT ITS REPLACEMENT. **
                                      *
                                      * ⬜ Pipeline's Branding screen does not write these two fields yet, so
                                      * every company takes the fallback — exactly as `--brand-header-bg`
                                      * behaves. Adding them there is a `symbia-pipeline` change, where
                                      * ⛔⛔ `git push` IS the deploy (decision 167).
                                      */
                                    className={`app-apptab${activeApp === app.id ? ' on' : ''}${tabOpen(app.id) ? '' : ' off'}`}
                                >
                                    {app.label}
                                </button>
                            ))}
                            {/* F802 (Pete F733: 'WMS tab can stay visible but disabled'): the C&W WMS, greyed
                                where the chosen company has no WMS (or the person no WMS role there). */}
                            {suiteCo && (
                                <button
                                    type="button"
                                    onClick={() => { if (wmsOpen) goToApp(withHint(WMS_URL, email)); }}
                                    disabled={!wmsOpen}
                                    title={wmsOpen ? 'Opens the C&W WMS' : `WMS is not set up for ${suiteCo.name}`}
                                    className={`app-apptab${wmsOpen ? '' : ' off'}`}
                                >
                                    WMS
                                </button>
                            )}
                        </nav>
                    </div>

                    {/**
                      * ⛔ CENTRED BY THE GRID, NOT BY margin:auto ON A FLEX CHILD.
                      * A flex-centred middle drifts as the left and right blocks
                      * change width — a long company name or a signed-in email
                      * would push it off centre, which is exactly the sort of
                      * "sometimes it looks wrong" nobody can reproduce.
                      */}
                    <h1 className="app-page-title" title={centreTitle}>{centreTitle}</h1>

                    {/**
                      * ⭐ `app-identity` CARRIES --brand-identity-* (migration 083).
                      * ⛔ Before it, the signed-in name, the email and the sign-out
                      * control had hardcoded near-blacks — so setting the header
                      * bar to a dark colour made the way OUT of the app nearly
                      * invisible. A branding setting must never be able to do that.
                      * ⚠️ The class goes on the WRAPPER: the CSS rule is
                      * `.app-identity, .app-identity *`, so a nested element with
                      * its own utility colour is still reached.
                      */}
                    <div className="app-identity flex items-center gap-3 justify-end">
                        <div className="text-right hidden sm:block">
                            <div className="font-medium leading-tight">{name || email}</div>
                            {name && (
                                <div className="text-muted-foreground leading-tight">{email}</div>
                            )}
                            <CompanyPicker me={me} />
                            <DefaultFacilityPicker getToken={getToken} />
                        </div>
                        <Button variant="ghost" size="sm" onClick={signOut} title="Sign out">
                            <LogOut className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                {/**
                  * ---- ⭐ TIER 2: THE VIEWS, IN THE STRIP THAT WAS ALREADY THERE -------
                  *
                  * ⭐ Pete, 2026-09-20: *"I like your apps on the brand row better.
                  * **conserves space**."* ⛔ So the APPS moved UP into the brand row's
                  * spare width (see above) and this strip — which was already here, and
                  * already had the gear — holds the views. **No row was added.**
                  *
                  * ⭐ AND `--brand-subnav-size` NOW MEANS WHAT ITS NAME SAYS. Migration
                  * 083 named it for the SUBNAV, and the subnav is these tabs. Pete asked
                  * for *"the size of the driver client carrier consignee — 10.5 px"*, so
                  * the **default** moved 13px → 10.5px in `index.css`.
                  * ⛔ NOT hard-coded on the button: `.app-tab` reads that variable, and
                  * a literal size here would silently defeat the setting for any customer
                  * who branded their strip.
                  *   ** A DEFAULT IS A SETTING'S FALLBACK, NOT ITS REPLACEMENT. CHANGE
                  *      THE FALLBACK; NEVER OVERRIDE THE VARIABLE. **
                  * ⚠️ 10.5px is `CHIP_TEXT.subject` in `ChipRow.tsx` — the size Pete
                  * settled on after four wrong rounds measured from his own console.
                  */}
                <div className="app-subnav">
                    <div className="app-subnav-inner is-wide">
                        <nav className="app-tabs">
                            {VIEW_TABS[activeApp].map((view) => (
                                <button
                                    key={view.id}
                                    type="button"
                                    className={`app-tab ${activeTab === view.id ? 'on' : ''}`}
                                    onClick={() => setActiveTab(view.id)}
                                >
                                    {view.label}
                                </button>
                            ))}
                        </nav>

                        {(me?.isScheduler || me?.isAdmin) && <GearMenu me={me} />}
                    </div>
                </div>
            </header>

            {/**
              * ⛔ #279 — `overflow-y-auto` IS THE OTHER HALF OF THE HEIGHT FIX, and
              * shipping the CSS without it would be worse than shipping neither.
              *
              * ⚠️ UNTIL NOW THE WINDOW SCROLLED, because nothing in the tree had a
              * real height and the page simply grew. Giving html/body/#root a height
              * takes that away — so any page taller than the viewport that does not
              * scroll ITSELF would now be silently CUT OFF at the fold. Several of
              * the admin forms are exactly that shape.
              *
              * ⭐ SO THE SCROLL MOVES FROM THE WINDOW INTO `main`, one level below
              * the header. Long forms scroll as they always did — the header now
              * stays put while they do, which is a small improvement nobody asked
              * for. Pages that manage their own scrolling (the calendar and its
              * rails) are unaffected: their `h-full` finally resolves to a real
              * number, which is the whole point of #279.
              *
              * ⛔ `min-h-0` STAYS AND IS NOT REDUNDANT. A flex child's default
              * `min-height: auto` refuses to shrink below its content, which would
              * push `main` past the viewport and put the scrollbar back on the
              * window — reintroducing the exact bug, with the CSS in place and
              * looking correct.
              */}
            <main className="flex-1 min-h-0 overflow-y-auto">
                {/**
                  * ---- ⛔⛔ THE CALENDAR IS NEVER UNMOUNTED (Pete, 2026-09-20) ---------
                  *
                  * ⭐ *"when you click on the reporting tab or claude tab and go back to
                  * the calendar tab, it doesn't keep the date (resets to current day)."*
                  *
                  * ⛔ THE DATE WAS NOT THE BUG, IT WAS THE SYMPTOM. `PageRouter` rendered
                  * `<CalendarPage/>` only on the calendar branch, so leaving the tab
                  * DESTROYED the component and everything in it — the date, the party
                  * scope, the status filters, the open rail panels, the paperwork tray's
                  * view and the scroll position. Coming back built a new one, and a new
                  * one starts on today. ⚠️ Only the date was visible enough to report.
                  *
                  * ⭐⭐ THIS IS THE RULE THIS CODEBASE ALREADY WROTE FOR ITSELF, in
                  * `CheckInPanel`'s two-column switch: *"⛔ NOT TWO SEPARATE RENDER
                  * BRANCHES. One tree, one set of props, no conditional mounting — so
                  * toggling never unmounts a panel and never resets an open card, a
                  * filter or the paperwork tray's view."*
                  *   ** HIDING IS NOT UNMOUNTING, AND A TAB IS A HIDE. **
                  *
                  * ⚠️ `hidden`, NOT A CONDITIONAL — and not `visibility` or an off-screen
                  * position either. `display:none` takes the subtree out of layout so it
                  * cannot scroll, trap focus or claim space, and ResizeObserver fires
                  * again when it comes back, so the Gantt re-measures `hourWidth` rather
                  * than staying at the zero width it saw while hidden.
                  *
                  * ⬜ ONE COST, ACCEPTED KNOWINGLY: a mounted calendar keeps its timers
                  * and its data. That is free today because every other view is a
                  * placeholder — ⛔ but §1AV.176 already records the rule for when they
                  * are not: **a background view must not fetch.** Revisit here, not
                  * after it bites.
                  */}
                <div className={activeTab === 'calendar' ? 'contents' : 'hidden'}>
                    <CalendarPage />
                </div>
                {activeTab !== 'calendar' && <PageRouter activeTab={activeTab} />}
            </main>

            <SchedulerChatWidget getToken={getToken} schedulerDisplayName={name || email} />
        </>
    );
}

function GearMenu({ me }: { me: { isScheduler: boolean; isAdmin: boolean } }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [open]);

    const handleItemClick = (settingsTab: SettingsTabId) => {
        setOpen(false);
        window.dispatchEvent(
            new CustomEvent('dockscheduler:open-settings', {
                detail: { tab: settingsTab },
            }),
        );
    };

    return (
        <div ref={wrapRef} className="gear-wrap">
            <button
                type="button"
                className="app-gear"
                onClick={() => setOpen((o) => !o)}
                aria-label="Settings"
                aria-expanded={open}
                aria-haspopup="menu"
                title="Settings"
            >
                <SettingsIcon className="h-4 w-4" />
            </button>

            {open && (
                <div className="gear-menu" role="menu">
                    {GEAR_MENU
                        .map((section) => ({
                            ...section,
                            items: section.items.filter((item) =>
                                item.requiredRole === 'admin' ? me.isAdmin : (me.isScheduler || me.isAdmin),
                            ),
                        }))
                        .filter((section) => section.items.length > 0)
                        .map((section, visibleIdx) => (
                            <div key={section.label}>
                                {visibleIdx > 0 && <div className="gear-divider" />}
                                <div className="gear-section-label">{section.label}</div>
                                {section.items.map((item) => (
                                    <button
                                        key={item.settingsTab}
                                        type="button"
                                        role="menuitem"
                                        className="gear-item"
                                        onClick={() => handleItemClick(item.settingsTab)}
                                    >
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        ))}
                </div>
            )}
        </div>
    );
}

/**
 * ⛔ A PLACEHOLDER THAT SAYS SO.
 *
 * ⚠️ THE FAILURE THIS AVOIDS IS A TAB THAT LOOKS BROKEN. An empty page and a page
 * that has not been built yet are indistinguishable to whoever clicks it, and the
 * first one generates a support call. So it names the feature, says what it is
 * for, and points at the open item that will fill it in.
 *
 * ⛔ AND IT DOES NOT FAKE THE CONTENT. A mocked-up chart or a chat box that does
 * nothing would be worse than blank: somebody would report it as not working, and
 * somebody else would spend an hour finding out there was nothing behind it.
 */
function ComingSoon({ blurb, item }: { blurb: string; item: string }) {
    /**
     * ⚠️ NO <h1> HERE EITHER. The header centre already shows this tab's name
     * (#251); a title on the page as well would print it twice.
     */
    return (
        <div className="flex flex-col h-full w-full gap-4 py-6 px-4 sm:px-6 2xl:px-8">
            <div className="rounded-xl border border-dashed border-gray-200 bg-white/50 p-8 text-center">
                <p className="text-sm text-muted-foreground">{blurb}</p>
                <p className="mt-2 text-xs text-muted-foreground/70">
                    Not built yet — tracked as {item}.
                </p>
            </div>
        </div>
    );
}

/**
 * ⭐ Decision 243 — what the Pipeline view shows when Pipeline's address is not
 * configured for this build. (~~a blocked new tab~~ can no longer happen: there is
 * no new tab.)
 */
function PipelineFallback() {
    /* ⭐ Decision 243: with an address the pill never lands here — it takes this tab
       to Pipeline. So this view means exactly one thing, and says it. */
    return (
        <div className="flex flex-col h-full w-full gap-4 py-6 px-4 sm:px-6 2xl:px-8">
            <div className="rounded-xl border border-dashed border-gray-200 bg-white/50 p-8 text-center">
                <p className="text-sm text-muted-foreground">
                    Pipeline's address is not configured for this build (VITE_PIPELINE_URL).
                </p>
            </div>
        </div>
    );
}

/**
 * ⛔ EVERY VIEW IN `VIEW_TABS` NEEDS A BRANCH HERE. The union is derived from that
 * object, so the compiler catches a view that has no branch — but ONLY because the
 * `default` below is a real fallback rather than a lie. ⚠️ A tab with no branch
 * renders the calendar, which is worse than an error: it looks like it worked.
 *
 * ⭐ THE FOUR PLACEHOLDERS SAY WHAT THEY WILL BE (Pete's 2026-08-25 rule, applied to
 * the new apps): *"a tab that opens a blank page reads as broken; a tab that says
 * 'not built yet, here is what it will be' reads as a roadmap."*
 * ⛔ AND THEY ARE HONEST ABOUT **WHY** THEY ARE NOT WIRED, because each one has a
 * different reason recorded in §1AV.177 — not one shared "coming soon".
 */
function PageRouter({ activeTab }: { activeTab: ViewTabId }) {
    switch (activeTab) {
        /* ⛔ `calendar` IS NOT HERE ANY MORE — AppShell mounts it permanently so
           switching tabs does not destroy the board's state. See the note there.
           ⚠️ This function is only reached when `activeTab !== 'calendar'`. */
        /* ⚠️ IP-gated + PIN, never Entra — §11.36.1. A PIN login nested inside an
           Entra session is two identity systems in one window, so the plan is a
           STAFF read-only view of the same screen, bounded by Entra identity and
           facility access. Never a kiosk session in a scheduler's browser. */
        case 'labor-mgmt':
            return (
                <ComingSoon
                    blurb="Labor management for a supervisor or facility manager — the Floor app. Its own application; no screens built yet."
                    item="§1AV.177"
                />
            );
        /* ⛔⤴ ~~'client-portal'~~ (a placeholder) — F218: the Client pill opens the real
           client portal (F212) in a new browser tab, so the Scheduler has no client view. */
        case 'pipeline-home':
            /* ⭐ Decision 243. Reached only when no Pipeline address is configured —
               see `chooseApp`. ⛔ It names the reason; a blank page here would read
               as Pipeline being down. */
            return <PipelineFallback />;
        /* ⭐ F186 (Decisions 271-299) — Pete: *"Dock Scheduler/Labor Reporting"* → the
           reporting engine's numbers, the same read as Pipeline's dock tiles. */
        case 'reports':
            return <ReportsPage />;
        /* ⭐ F161 — the floor board, inside the Scheduler (Decision 334). Mounted only
           while this tab is open, so it polls only then (§1AV.176: a background view
           must not fetch). */
        case 'warehouse':
            return <SignedInWarehouseBoard cfg={WAREHOUSE_BOARD_CONFIG} />;
        /* ⛔⤴ ~~'claude'~~ — F218, Pete: *"Lets remove the Claude link on the dock scheduler
           app"*. The idea (#252, ask the board in plain English) stays in the open items. */
        /**
         * ⛔⛔ THE DEFAULT NO LONGER RENDERS THE CALENDAR, AND THAT IS THE POINT.
         * It used to, which meant a view added to `VIEW_TABS` with no branch here
         * showed the dock board — **it looked like it worked.** A tab that renders the
         * wrong screen is worse than one that renders an honest gap, because nobody
         * files a bug against a board that looks fine.
         *   ** A `default` THAT RENDERS SOMETHING PLAUSIBLE HIDES THE CASE YOU FORGOT. **
         * ⚠️ `activeTab` is a closed union, so the compiler already catches a MISSING
         * branch; this is the runtime half, for a value that arrives from state.
         */
        default:
            return (
                <ComingSoon
                    blurb="This view has no screen wired to it yet. If you are seeing this after selecting a real tab, that tab is missing a branch in PageRouter."
                    item="PageRouter"
                />
            );
    }
}

